import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, ExternalLink, ThumbsUp, MessageSquare, Eye, Share2, Clock } from "lucide-react";
import { fetchSocialPosts, TAG_NAMES, TAG_COLORS } from "@/lib/api";

const PERIOD_OPTIONS = [
  { value: '24h', label: 'Last 24 Hours' },
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
];

export default function SocialDetailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const platform = searchParams.get('platform') || 'reddit';
  const keyword = searchParams.get('keyword') || '';
  const category = searchParams.get('category') || '';
  const initialPeriod = searchParams.get('period') || '30d';

  const [period, setPeriod] = useState(initialPeriod);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const platformColor = platform === 'reddit' ? '#FF4500' : '#00F2EA';
  const catColor = TAG_COLORS[category] || '#6B7280';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchSocialPosts({ platform, category, period, limit: 50 });
      setData(res);
      if (res.error) setError(res.error);
    } catch (e) {
      console.error(e);
      setError('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [platform, keyword, period]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="social-detail-page">
      {/* Back + Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/social')}
          className="flex items-center gap-2 text-zinc-400 hover:text-white transition-colors font-display text-sm uppercase tracking-wider"
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="h-5 w-px bg-white/10" />
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: platformColor }} />
          <span className="font-display text-sm font-bold uppercase tracking-wider text-white">
            {platform === 'reddit' ? 'Reddit' : 'TikTok'}
          </span>
        </div>
        {category && TAG_NAMES[category] && (
          <>
            <div className="h-5 w-px bg-white/10" />
            <Badge className="rounded-sm text-[9px] font-mono-data border-none px-2 py-0.5"
              style={{ background: `${catColor}22`, color: catColor }}>
              {TAG_NAMES[category]}
            </Badge>
          </>
        )}
      </div>

      {/* Keyword + Period Selector */}
      <Card className="glass-card border-zinc-800 rounded-none">
        <CardContent className="py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="font-mono-data text-[10px] text-zinc-600 uppercase tracking-widest mb-1">Search Term</p>
              <p className="font-display text-lg font-bold text-white">{keyword}</p>
            </div>
            <div className="flex gap-1">
              {PERIOD_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setPeriod(opt.value)}
                  className={`px-4 py-2 text-xs font-display uppercase tracking-wider font-semibold transition-all duration-200 border ${
                    period === opt.value
                      ? 'text-white border-current'
                      : 'text-zinc-600 border-white/5 hover:text-zinc-300'
                  }`}
                  style={period === opt.value ? { color: platformColor, borderColor: platformColor, background: `${platformColor}12` } : {}}
                  data-testid={`period-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <div className="p-4 border border-yellow-500/20 bg-yellow-500/5">
          <p className="font-mono-data text-xs text-yellow-400">{error}</p>
        </div>
      )}

      {/* Results */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => <div key={i} className="h-16 bg-zinc-900/50 animate-pulse rounded-none" />)}
        </div>
      ) : platform === 'reddit' ? (
        <RedditResults posts={data?.posts || []} total={data?.total || 0} platformColor={platformColor} />
      ) : (
        <TikTokResults videos={data?.videos || []} total={data?.total || 0} platformColor={platformColor} />
      )}
    </div>
  );
}

function RedditResults({ posts, total, platformColor }) {
  return (
    <Card className="glass-card border-zinc-800 rounded-none" data-testid="reddit-results">
      <CardHeader className="pb-2 border-b border-white/5">
        <CardTitle className="font-display text-sm font-semibold tracking-wider uppercase text-zinc-300 flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: platformColor }} />
          {total} {total === 1 ? 'Post' : 'Posts'} Found
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {posts.length === 0 ? (
          <div className="py-12 text-center">
            <Clock className="w-6 h-6 text-zinc-700 mx-auto mb-2" />
            <p className="font-mono-data text-sm text-zinc-600">No posts found for this period</p>
          </div>
        ) : (
          posts.map((post, i) => (
            <div key={i} className="feed-item flex items-start gap-3 animate-fade-in-up" style={{ animationDelay: `${i * 20}ms` }} data-testid={`reddit-post-${i}`}>
              <div className="flex flex-col items-center flex-shrink-0 w-12 text-center">
                <ThumbsUp className="w-3 h-3 text-[#FF4500] mb-0.5" />
                <span className="font-mono-data text-xs font-bold text-zinc-300">{post.score}</span>
              </div>
              <div className="flex-1 min-w-0">
                <a href={post.url} target="_blank" rel="noopener noreferrer" className="text-sm text-zinc-200 hover:text-white transition-colors duration-200 flex items-center gap-1.5">
                  <span className="line-clamp-2">{post.title || 'Untitled'}</span>
                  <ExternalLink className="w-3 h-3 flex-shrink-0 text-zinc-600" />
                </a>
                <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                  <span className="font-mono-data text-[10px] text-[#FF4500]">r/{post.subreddit}</span>
                  <span className="font-mono-data text-[10px] text-zinc-600 flex items-center gap-1">
                    <MessageSquare className="w-2.5 h-2.5" /> {post.num_comments}
                  </span>
                  <span className="font-mono-data text-[9px] text-zinc-700">u/{post.author}</span>
                  {post.created_utc > 0 && (
                    <span className="font-mono-data text-[9px] text-zinc-700">
                      {new Date(post.created_utc * 1000).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  )}
                </div>
                {post.selftext && (
                  <p className="font-mono-data text-[10px] text-zinc-600 mt-1.5 line-clamp-2">{post.selftext}</p>
                )}
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function TikTokResults({ videos, total, platformColor }) {
  return (
    <Card className="glass-card border-zinc-800 rounded-none" data-testid="tiktok-results">
      <CardHeader className="pb-2 border-b border-white/5">
        <CardTitle className="font-display text-sm font-semibold tracking-wider uppercase text-zinc-300 flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full" style={{ background: platformColor }} />
          {total} {total === 1 ? 'Video' : 'Videos'} Found
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {videos.length === 0 ? (
          <div className="py-12 text-center">
            <Clock className="w-6 h-6 text-zinc-700 mx-auto mb-2" />
            <p className="font-mono-data text-sm text-zinc-600">No videos found for this period</p>
          </div>
        ) : (
          videos.map((vid, i) => (
            <div key={i} className="feed-item flex items-start gap-3 animate-fade-in-up" style={{ animationDelay: `${i * 20}ms` }} data-testid={`tiktok-video-${i}`}>
              <div className="flex flex-col items-center flex-shrink-0 w-14 text-center">
                <Eye className="w-3 h-3 text-[#00F2EA] mb-0.5" />
                <span className="font-mono-data text-xs font-bold text-zinc-300">{formatNum(vid.views)}</span>
              </div>
              <div className="flex-1 min-w-0">
                <a href={vid.url} target="_blank" rel="noopener noreferrer" className="text-sm text-zinc-200 hover:text-white transition-colors duration-200 flex items-center gap-1.5">
                  <span className="line-clamp-2">{vid.title || 'Untitled'}</span>
                  <ExternalLink className="w-3 h-3 flex-shrink-0 text-zinc-600" />
                </a>
                <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                  <span className="font-mono-data text-[10px] text-[#00F2EA]">@{vid.author}</span>
                  <span className="font-mono-data text-[10px] text-zinc-600 flex items-center gap-1">
                    <ThumbsUp className="w-2.5 h-2.5" /> {formatNum(vid.likes)}
                  </span>
                  <span className="font-mono-data text-[10px] text-zinc-600 flex items-center gap-1">
                    <MessageSquare className="w-2.5 h-2.5" /> {formatNum(vid.comments)}
                  </span>
                  <span className="font-mono-data text-[10px] text-zinc-600 flex items-center gap-1">
                    <Share2 className="w-2.5 h-2.5" /> {formatNum(vid.shares)}
                  </span>
                </div>
              </div>
            </div>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function formatNum(n) {
  if (!n) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}
